/**
 * VISIT-TASK-001 · Frontend companion of `backend/core/visit_task_mapping.py`.
 *
 * Kept intentionally small and additive:
 *  * `NEW_BOOKING_VISIT_TYPES` — the vocabulary offered by the new
 *    booking form (legacy "Technical Survey" / "Maintenance" are
 *    hidden but the backend keeps accepting them on writes).
 *  * `VISIT_TASK_MULTISELECT_TYPES` — visit types where the user
 *    must pick the applicable canonical task_key subset.
 *  * `VISIT_TASK_ELIGIBLE_KEYS[type]` — the superset of canonical
 *    keys per visit type. The form intersects this with the current
 *    project's phase tasks to render only *applicable* options.
 *  * `VISIT_TASK_TITLES[key]` — human labels for the eligible keys.
 *  * `normaliseAvailableTaskKeys(project, visitType)` — filters the
 *    eligible superset by (a) presence on the project and (b) that
 *    the task is not already `complete`.
 */

export const NEW_BOOKING_VISIT_TYPES = [
    "Initial Survey",
    "Site Survey",
    "Scaffolding",
    "Delivery",
    "Installation",
    "Electrical Work",
    "Commissioning",
    "Handover",
    "Service",
    "Other",
];

export const VISIT_TASK_MULTISELECT_TYPES = new Set([
    "Installation", "Commissioning", "Handover",
]);

export const VISIT_TYPE_REQUIRES_DESCRIPTION = new Set(["Service", "Other"]);
export const VISIT_TYPE_REQUIRES_SCAFFOLDING_VARIANT = new Set(["Scaffolding"]);

export const VISIT_TASK_ELIGIBLE_KEYS = {
    Installation: [
        "installation.panels_mounted",
        "installation.inverter_installed",
        "installation.battery_installed",
        "installation.wiring_complete",
    ],
    Commissioning: [
        "commissioning.system_energised",
        "commissioning.performance_tested",
        "commissioning.monitoring_configured",
    ],
    Handover: [
        "handover.pack_delivered",
        "handover.warranty_provided",
    ],
};

export const VISIT_TASK_TITLES = {
    "installation.panels_mounted":       "Panels mounted",
    "installation.inverter_installed":   "Inverter installed",
    "installation.battery_installed":    "Battery installed",
    "installation.wiring_complete":      "Wiring complete",
    "commissioning.system_energised":    "System energised",
    "commissioning.performance_tested":  "Performance tested",
    "commissioning.monitoring_configured": "Monitoring configured",
    "handover.pack_delivered":           "Handover pack delivered",
    "handover.warranty_provided":        "Warranty provided",
    "site_survey.visit_complete":        "Site survey completed",
    "sales.initial_survey":              "Initial survey completed",
    "logistics.scaffolding.erect":       "Scaffolding erected",
    "logistics.scaffolding.remove":      "Scaffolding removed",
};

export function isCanonicalVisitType(type) {
    return (
        VISIT_TASK_MULTISELECT_TYPES.has(type)
        || type === "Site Survey"
        || type === "Initial Survey"
        || type === "Scaffolding"
    );
}

export function normaliseAvailableTaskKeys(project, visitType) {
    const superset = VISIT_TASK_ELIGIBLE_KEYS[visitType] || [];
    if (!project || !Array.isArray(project.phases)) return [];
    const found = new Set();
    for (const phase of project.phases) {
        for (const task of phase.tasks || []) {
            if (!task || !task.task_key) continue;
            if (task.status === "complete") continue;
            if (superset.includes(task.task_key)) found.add(task.task_key);
        }
    }
    // Preserve the canonical order defined in the superset.
    return superset.filter((k) => found.has(k));
}
